import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  TextField,
  IconButton,
  Avatar,
  Typography,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Divider,
  Badge,
  Chip,
  InputAdornment,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Select,
  FormControl,
  InputLabel,
  Tooltip,
  ListItemSecondaryAction,
  Tabs,
  Tab,
  Collapse,
  AvatarGroup
} from '@mui/material';
import {
  Send,
  Search,
  AttachFile,
  MoreVert,
  Close,
  Image as ImageIcon,
  Description,
  EmojiEmotions,
  Check,
  DoneAll,
  AccessTime,
  Campaign,
  LocalOffer,
  Assignment,
  WhatsApp,
  KeyboardArrowDown,
  Person,
  Add,
  Archive,
  ExpandMore,
  ExpandLess,
  Star,
  StarBorder,
  Group,
  Label,
  VolumeOff,
  Mic,
  Phone,
  Videocam,
  PhotoCamera
} from '@mui/icons-material';
import axios from 'axios';
import { HEADERS, HEADER_FORM_DATA } from '../../../API/UrlAndPaths';
import KooppaiIcon from './KooppaiIcon';

// Provider configurations
const PROVIDERS = [
  { id: 'KOOPPAI', name: 'KooppAI', icon: <KooppaiIcon />, color: '#6366f1' },
  { id: 'WHATSAPP', name: 'WhatsApp', icon: <WhatsApp />, color: '#25D366' }
];

// Chat tag types with icons
const CHAT_TAGS = {
  CAMPAIGN: { icon: <Campaign fontSize="small" />, color: '#9c27b0', label: 'Campaign' },
  OFFER: { icon: <LocalOffer fontSize="small" />, color: '#4caf50', label: 'Offer' },
  FORM: { icon: <Assignment fontSize="small" />, color: '#ff9800', label: 'Form' },
  LEAD: { icon: <Person fontSize="small" />, color: '#2196f3', label: 'Lead' }
};

// Filter tabs configuration
const FILTER_TABS = [
  { id: 'all', label: 'All' },
  { id: 'unread', label: 'Unread' },
  { id: 'favourites', label: 'Favourites' },
  { id: 'groups', label: 'Groups' },
  { id: 'labels', label: 'Labels' }
];

const normalizeMediaUrl = (path) => {
  if (!path) return '';
  // If already absolute (http/https), use as-is
  if (/^https?:\/\//i.test(path)) return path;
  // Use axios baseURL (set in UrlAndPaths.js)
  const baseUrl = axios.defaults.baseURL?.replace(/\/$/, '') || '';
  const cleanPath = path.startsWith('/') ? path : `/${path}`;
  return `${baseUrl}${cleanPath}`;
};

const WhatsAppChat = () => {
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState('KOOPPAI');
  const [providerMenuAnchor, setProviderMenuAnchor] = useState(null);
  const [quickActionsAnchor, setQuickActionsAnchor] = useState(null);
  const [activeFilter, setActiveFilter] = useState('all');
  const [archivedExpanded, setArchivedExpanded] = useState(false);
  const [labelsMenuAnchor, setLabelsMenuAnchor] = useState(null);
  const [contextMenu, setContextMenu] = useState({ open: false, anchorEl: null, conversation: null });
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  
  // Quick send dialogs
  const [campaignDialog, setCampaignDialog] = useState(false);
  const [offerDialog, setOfferDialog] = useState(false);
  const [formDialog, setFormDialog] = useState(false);
  const [campaigns, setCampaigns] = useState([]);
  const [offers, setOffers] = useState([]);
  const [forms, setForms] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [sending, setSending] = useState(false);

  // Handle right-click context menu
  const handleContextMenu = (event, conv) => {
    event.preventDefault();
    setContextMenu({
      open: true,
      anchorEl: event.currentTarget,
      conversation: conv,
      mouseX: event.clientX,
      mouseY: event.clientY
    });
  };

  // Build a rich offer message for quick send, similar to SendOfferDialog
  const buildQuickOfferMessage = (offer) => {
    if (!offer) return '';

    let message = `🎉 *${offer.title}*\n\n`;

    if (offer.description) {
      message += `${offer.description}\n\n`;
    }

    if (offer.type === 'PERCENTAGE') {
      message += `💰 Get *${offer.value}% OFF*\n`;
    } else if (offer.type === 'FIXED_AMOUNT') {
      message += `💰 Get *₹${offer.value} OFF*\n`;
    } else if (offer.type === 'FREE_DELIVERY') {
      message += `🚚 *FREE DELIVERY*\n`;
    }

    if (offer.minOrderValue) {
      message += `📋 Min order: ₹${offer.minOrderValue}\n`;
    }

    if (offer.code) {
      message += `\n🏷️ *Promo Code:* \`${offer.code}\`\n`;
    }

    if (offer.endDate) {
      message += `⏰ Valid till: ${new Date(offer.endDate).toLocaleDateString()}\n`;
    }

    return message;
  };

  const handleAttachClick = () => {
    if (!selectedConversation) return;
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file || !selectedConversation) return;

    try {
      const formData = new FormData();
      formData.append('file', file);
      if (newMessage && newMessage.trim()) {
        formData.append('message', newMessage.trim());
      }

      const res = await axios.post(
        `/api/crm-chat/conversations/${selectedConversation._id}/messages/attachment`,
        formData,
        HEADER_FORM_DATA
      );

      if (res.data.success) {
        setMessages(prev => [...prev, res.data.data]);
        setNewMessage('');
        fetchConversations();
      }
    } catch (error) {
      console.error('Error sending attachment:', error);
    } finally {
      e.target.value = '';
    }
  };

  const handleCloseContextMenu = () => {
    setContextMenu({ open: false, anchorEl: null, conversation: null });
  };

  // Fetch provider settings on mount
  useEffect(() => {
    fetchProviderSettings();
  }, []);

  // Fetch conversations when provider changes
  useEffect(() => {
    fetchConversations();
  }, [selectedProvider]);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Fetch provider settings from notification config
  const fetchProviderSettings = async () => {
    try {
      const res = await axios.get('/api/notifications/config', HEADERS);
      if (res.data?.success || res.data?.data) {
        const config = res.data.data || res.data;
        // Check which providers are enabled
        const whatsappEnabled = config.whatsapp?.enabled && 
          (config.whatsapp?.qrLinked || config.whatsapp?.provider !== 'WHATSAPP_WEB');
        const kooppaiEnabled = config.kooppai?.enabled;
        
        // Set default provider based on settings
        if (whatsappEnabled) {
          setSelectedProvider('WHATSAPP');
        } else if (kooppaiEnabled) {
          setSelectedProvider('KOOPPAI');
        }
      }
    } catch (error) {
      console.log('Could not fetch provider settings, using default');
    }
  };

  const fetchConversations = async () => {
    try {
      setLoading(true);
      // Fetch conversations for the selected provider so WhatsApp and KooppAI
      // have separate chat lists and message flows
      const res = await axios.get(
        `/api/crm-chat/conversations?provider=${selectedProvider}`,
        HEADERS
      );
      if (res.data.success && res.data.data) {
        setConversations(res.data.data);
      } else {
        setConversations([]);
      }
    } catch (error) {
      console.error('Error fetching conversations:', error);
      setConversations([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch campaigns for quick send
  const fetchCampaigns = async () => {
    try {
      // Fetch all campaigns (not just ACTIVE to show more options)
      const res = await axios.get('/api/campaigns', HEADERS);
      console.log('Campaigns response:', res.data);
      if (res.data.data) {
        setCampaigns(res.data.data || []);
      } else if (Array.isArray(res.data)) {
        setCampaigns(res.data);
      }
    } catch (error) {
      console.error('Error fetching campaigns:', error);
    }
  };

  // Fetch offers for quick send
  const fetchOffers = async () => {
    try {
      // Fetch all offers (same endpoint as OffersManagement)
      const res = await axios.get('/api/crm/offers', HEADERS);
      console.log('Offers response:', res.data);
      if (res.status === 200 && res.data.data) {
        setOffers(res.data.data || []);
      } else {
        setOffers([]);
      }
    } catch (error) {
      console.error('Error fetching offers:', error);
    }
  };

  // Fetch forms for quick send
  const fetchForms = async () => {
    try {
      // Fetch all forms (isActive filter may not be set on all forms)
      const res = await axios.get('/api/crm-forms/templates', HEADERS);
      console.log('Forms response:', res.data);
      if (res.data.data) {
        setForms(res.data.data || []);
      } else if (Array.isArray(res.data)) {
        setForms(res.data);
      }
    } catch (error) {
      console.error('Error fetching forms:', error);
    }
  };

  // Open campaign dialog
  const handleOpenCampaignDialog = () => {
    fetchCampaigns();
    setSelectedItem(null);
    setCampaignDialog(true);
  };

  // Open offer dialog
  const handleOpenOfferDialog = () => {
    fetchOffers();
    setSelectedItem(null);
    setOfferDialog(true);
  };

  // Open form dialog
  const handleOpenFormDialog = () => {
    fetchForms();
    setSelectedItem(null);
    setFormDialog(true);
  };

  // Send campaign to current customer
  const handleSendCampaign = async () => {
    if (!selectedItem || !selectedConversation) return;
    setSending(true);
    try {
      // Use CRM campaign send endpoint; it will use campaign.targetCustomerIds.
      const res = await axios.post(`/api/crm/campaigns/${selectedItem._id}/send`, {
        channel: selectedProvider,
        // Override target customers so this quick-send only hits the current chat customer
        customerIds: selectedConversation.customerId ? [selectedConversation.customerId] : []
      }, HEADERS);
      if (res.data.success) {
        alert('Campaign sent successfully!');
        fetchMessages(selectedConversation._id);
      }
    } catch (error) {
      console.error('Error sending campaign:', error);
      alert('Failed to send campaign');
    } finally {
      setSending(false);
      setCampaignDialog(false);
      setSelectedItem(null);
    }
  };

  // Send offer to current customer
  const handleSendOffer = async () => {
    if (!selectedItem || !selectedConversation) return;
    setSending(true);
    try {
      const baseMessage = buildQuickOfferMessage(selectedItem);
      // Use CRM offers send endpoint; it expects a customers array with mobileNo/email.
      const res = await axios.post(`/api/crm/offers/${selectedItem._id}/send`, {
        customers: [{
          _id: selectedConversation.customerId,
          name: selectedConversation.customerName,
          mobileNo: selectedConversation.customerPhone,
          email: selectedConversation.customerEmail
        }],
        channel: selectedProvider,
        message: selectedItem.message || `${baseMessage}\n\nHi {{name}}, don't miss this offer!`
      }, HEADERS);
      if (res.data.success) {
        alert('Offer sent successfully!');
        fetchMessages(selectedConversation._id);
      }
    } catch (error) {
      console.error('Error sending offer:', error);
      alert('Failed to send offer');
    } finally {
      setSending(false);
      setOfferDialog(false);
      setSelectedItem(null);
    }
  };

  // Send form to current customer
  const handleSendForm = async () => {
    if (!selectedItem || !selectedConversation) return;
    setSending(true);
    try {
      // Build form link
      const baseUrl = window.location.origin;
      const formLink = `${baseUrl}/crm/form/${selectedItem._id}`;
      const formTitle = selectedItem.title || selectedItem.name || 'Form';
      
      const res = await axios.post(`/api/crm-forms/send`, {
        formId: selectedItem._id,
        formTitle,
        formLink: formLink,
        customers: [{
          mobileNo: selectedConversation.customerPhone,
          name: selectedConversation.customerName,
          email: selectedConversation.customerEmail
        }],
        channel: selectedProvider,
        message: `Please fill out this form: ${formTitle}`
      }, HEADERS);
      if (res.data.success || res.data.status === 'SUCCESS') {
        alert('Form sent successfully!');
        fetchMessages(selectedConversation._id);
      }
    } catch (error) {
      console.error('Error sending form:', error);
      alert('Failed to send form');
    } finally {
      setSending(false);
      setFormDialog(false);
      setSelectedItem(null);
    }
  };

  // Load demo conversations for testing
  const loadDemoConversations = () => {
    setConversations([
        {
          _id: '1',
          customerName: 'PK Shafeeque Contact (You)',
          customerPhone: '+91 98765 43210',
          lastMessage: '6802268073ed77ddf1e392e',
          lastMessageAt: new Date(),
          unreadCount: 0,
          tags: ['LEAD'],
          provider: selectedProvider,
          isFavourite: true,
          isGroup: false,
          isArchived: false,
          messageType: 'text',
          avatar: null
        },
        {
          _id: '2',
          customerName: 'CAFITians',
          customerPhone: '+91 87654 32109',
          lastMessage: 'Cyberpark CAFIT Sec Gaffor Vk: Photo',
          lastMessageAt: new Date(),
          unreadCount: 20,
          tags: ['FORM'],
          provider: selectedProvider,
          isFavourite: false,
          isGroup: true,
          isArchived: false,
          messageType: 'photo',
          isMuted: true,
          avatar: null
        },
        {
          _id: '3',
          customerName: 'Dxb Habib (DXB Leeyet Partner) Noorucha Br...',
          customerPhone: '+91 76543 21098',
          lastMessage: '0:18',
          lastMessageAt: new Date(Date.now() - 3600000),
          unreadCount: 0,
          tags: [],
          provider: selectedProvider,
          isFavourite: false,
          isGroup: false,
          isArchived: false,
          messageType: 'voice',
          avatar: null
        },
        {
          _id: '4',
          customerName: '+91 97477 14788',
          customerPhone: '+91 97477 14788',
          lastMessage: 'Voice call',
          lastMessageAt: new Date(Date.now() - 7200000),
          unreadCount: 0,
          tags: [],
          provider: selectedProvider,
          isFavourite: false,
          isGroup: false,
          isArchived: false,
          messageType: 'call',
          avatar: null
        },
        {
          _id: '5',
          customerName: 'Sez Startup Cunsltant Faisal Cs Sinju Balu KK',
          customerPhone: '+91 98765 12345',
          lastMessage: '0:02',
          lastMessageAt: new Date(Date.now() - 10800000),
          unreadCount: 0,
          tags: [],
          provider: selectedProvider,
          isFavourite: true,
          isGroup: false,
          isArchived: false,
          messageType: 'voice',
          avatar: null
        },
        {
          _id: '6',
          customerName: 'PM Siddik',
          customerPhone: '+91 87654 98765',
          lastMessage: '0:04',
          lastMessageAt: new Date(Date.now() - 14400000),
          unreadCount: 1,
          tags: [],
          provider: selectedProvider,
          isFavourite: false,
          isGroup: false,
          isArchived: false,
          messageType: 'voice',
          avatar: null
        },
        {
          _id: 'archived1',
          customerName: 'Old Customer',
          customerPhone: '+91 11111 22222',
          lastMessage: 'Thanks!',
          lastMessageAt: new Date(Date.now() - 86400000 * 7),
          unreadCount: 0,
          tags: ['OFFER'],
          provider: selectedProvider,
          isFavourite: false,
          isGroup: false,
          isArchived: true,
          messageType: 'text',
          avatar: null
        }
      ]);
  };

  // Get current provider config
  const currentProvider = PROVIDERS.find(p => p.id === selectedProvider) || PROVIDERS[0];

  // Handle provider change
  const handleProviderChange = (providerId) => {
    setSelectedProvider(providerId);
    setProviderMenuAnchor(null);
    setSelectedConversation(null);
    setMessages([]);
  };

  // Render chat tags - WhatsApp style badges
  const renderChatTags = (tags) => {
    if (!tags || tags.length === 0) return null;
    return (
      <>
        {tags.slice(0, 1).map((tag, idx) => {
          const tagConfig = CHAT_TAGS[tag];
          if (!tagConfig) return null;
          return (
            <Chip
              key={idx}
              label={tagConfig.label}
              size="small"
              sx={{
                height: 20,
                fontSize: 11,
                fontWeight: 500,
                bgcolor: `${tagConfig.color}`,
                color: '#fff',
                borderRadius: '4px',
                '& .MuiChip-label': { px: 1 }
              }}
            />
          );
        })}
      </>
    );
  };

  // Render message type icon (voice, photo, video, call)
  const renderMessageTypeIcon = (conv) => {
    switch (conv.messageType) {
      case 'voice':
        return <Mic sx={{ fontSize: 14, color: '#667781', mr: 0.5 }} />;
      case 'photo':
        return <PhotoCamera sx={{ fontSize: 14, color: '#667781', mr: 0.5 }} />;
      case 'video':
        return <Videocam sx={{ fontSize: 14, color: '#667781', mr: 0.5 }} />;
      case 'call':
        return <Phone sx={{ fontSize: 14, color: '#667781', mr: 0.5 }} />;
      default:
        return null;
    }
  };

  // Filter conversations based on active filter and selected provider
  const getFilteredConversations = () => {
    let filtered = conversations
      .filter(conv => !conv.isArchived)
      .filter(conv => !conv.provider || conv.provider === selectedProvider);
    
    // Apply search filter
    if (searchQuery) {
      filtered = filtered.filter(conv =>
        conv.customerName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        conv.customerPhone?.includes(searchQuery) ||
        conv.lastMessage?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    
    // Apply tab filter
    switch (activeFilter) {
      case 'unread':
        return filtered.filter(conv => conv.unreadCount > 0);
      case 'favourites':
        return filtered.filter(conv => conv.isFavourite);
      case 'groups':
        return filtered.filter(conv => conv.isGroup);
      case 'labels':
        return filtered.filter(conv => conv.tags && conv.tags.length > 0);
      default:
        return filtered;
    }
  };

  // Get archived conversations
  const archivedConversations = conversations.filter(conv => conv.isArchived);
  const archivedCount = archivedConversations.length;

  const fetchMessages = async (conversationId) => {
    try {
      const res = await axios.get(`/api/crm-chat/conversations/${conversationId}`, HEADERS);
      if (res.data.success) {
        setSelectedConversation(res.data.data.conversation);
        setMessages(res.data.data.messages);
      }
    } catch (error) {
      console.error('Error fetching messages:', error);
      // Mock messages for demo
      const conv = conversations.find(c => c._id === conversationId);
      if (conv) {
        setSelectedConversation(conv);
        setMessages([
          {
            _id: '1',
            content: 'Hello! How can I help you today?',
            senderType: 'STAFF',
            timestamp: new Date(Date.now() - 3600000),
            status: 'READ'
          },
          {
            _id: '2',
            content: conv.lastMessage,
            senderType: 'CUSTOMER',
            senderName: conv.customerName,
            timestamp: conv.lastMessageAt,
            status: 'DELIVERED'
          }
        ]);
      }
    }
  };

  // Toggle conversation property (favourite, archive, mute)
  const toggleConversationProperty = async (conversationId, property) => {
    try {
      const res = await axios.patch(
        `/api/crm-chat/conversations/${conversationId}/toggle`,
        { property },
        HEADERS
      );
      if (res.data.success) {
        fetchConversations();
      }
    } catch (error) {
      console.error('Error toggling property:', error);
      // Update locally for demo
      setConversations(prev => prev.map(conv => 
        conv._id === conversationId 
          ? { ...conv, [property]: !conv[property] }
          : conv
      ));
    }
  };

  // Delete conversation
  const deleteConversation = async (conversationId) => {
    if (!window.confirm('Are you sure you want to delete this chat?')) return;
    
    try {
      const res = await axios.delete(
        `/api/crm-chat/conversations/${conversationId}`,
        HEADERS
      );
      if (res.data.success) {
        fetchConversations();
        if (selectedConversation?._id === conversationId) {
          setSelectedConversation(null);
          setMessages([]);
        }
      }
    } catch (error) {
      console.error('Error deleting conversation:', error);
      // Remove locally for demo
      setConversations(prev => prev.filter(conv => conv._id !== conversationId));
      if (selectedConversation?._id === conversationId) {
        setSelectedConversation(null);
        setMessages([]);
      }
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !selectedConversation) return;

    try {
      const res = await axios.post(
        `/api/crm-chat/conversations/${selectedConversation._id}/messages`,
        {
          content: newMessage,
          messageType: 'TEXT',
          senderType: 'STAFF'
        },
        HEADERS
      );

      if (res.data.success) {
        setMessages([...messages, res.data.data]);
        setNewMessage('');
        fetchConversations(); // Refresh conversation list
      }
    } catch (error) {
      console.error('Error sending message:', error);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Format time for chat list (WhatsApp style: "2:27 pm" for today, date for older)
  const formatTime = (date) => {
    const d = new Date(date);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (d.toDateString() === today.toDateString()) {
      return d.toLocaleTimeString('en-US', { 
        hour: 'numeric', 
        minute: '2-digit',
        hour12: true 
      }).toLowerCase();
    }
    if (d.toDateString() === yesterday.toDateString()) {
      return 'Yesterday';
    }
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const formatDate = (date) => {
    const d = new Date(date);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (d.toDateString() === today.toDateString()) return 'Today';
    if (d.toDateString() === yesterday.toDateString()) return 'Yesterday';
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'SENT':
        return <Check sx={{ fontSize: 16, color: '#8696a0' }} />;
      case 'DELIVERED':
        return <DoneAll sx={{ fontSize: 16, color: '#8696a0' }} />;
      case 'READ':
        return <DoneAll sx={{ fontSize: 16, color: '#53bdeb' }} />;
      default:
        return <AccessTime sx={{ fontSize: 16, color: '#8696a0' }} />;
    }
  };

  // Use the new filter function
  const filteredConversations = getFilteredConversations();

  return (
    <Box sx={{ display: 'flex', height: 'calc(100vh - 100px)', bgcolor: '#f0f2f5' }}>
      {/* Left Sidebar - Conversations List */}
      <Paper
        sx={{
          width: 350,
          minWidth: 350,
          maxWidth: 350,
          flexShrink: 0,
          display: 'flex',
          flexDirection: 'column',
          borderRadius: 0,
          borderRight: '1px solid #e9edef'
        }}
      >
        {/* Header with Provider Selector */}
        <Box sx={{ p: 2, bgcolor: '#f0f2f5', borderBottom: '1px solid #e9edef' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#111b21' }}>
              Chats
            </Typography>
            {/* Provider Selector */}
            <Button
              variant="outlined"
              size="small"
              onClick={(e) => setProviderMenuAnchor(e.currentTarget)}
              startIcon={currentProvider.icon}
              endIcon={<KeyboardArrowDown />}
              sx={{
                borderColor: currentProvider.color,
                color: currentProvider.color,
                textTransform: 'none',
                '&:hover': {
                  borderColor: currentProvider.color,
                  bgcolor: `${currentProvider.color}10`
                }
              }}
            >
              {currentProvider.name}
            </Button>
          </Box>
        </Box>

        {/* Provider Selection Menu */}
        <Menu
          anchorEl={providerMenuAnchor}
          open={Boolean(providerMenuAnchor)}
          onClose={() => setProviderMenuAnchor(null)}
          PaperProps={{ sx: { minWidth: 180 } }}
        >
          {PROVIDERS.map((provider) => (
            <MenuItem
              key={provider.id}
              selected={selectedProvider === provider.id}
              onClick={() => handleProviderChange(provider.id)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                '&.Mui-selected': {
                  bgcolor: `${provider.color}15`
                }
              }}
            >
              <Box sx={{ color: provider.color, display: 'flex', alignItems: 'center' }}>
                {provider.icon}
              </Box>
              <Typography>{provider.name}</Typography>
              {selectedProvider === provider.id && (
                <Check sx={{ ml: 'auto', color: provider.color }} fontSize="small" />
              )}
            </MenuItem>
          ))}
        </Menu>

        {/* Search */}
        <Box sx={{ px: 2, py: 1, bgcolor: '#fff' }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Search or start a new chat"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ color: '#54656f' }} />
                </InputAdornment>
              ),
              sx: {
                bgcolor: '#f0f2f5',
                borderRadius: 2,
                height: 35,
                '& fieldset': { border: 'none' },
                '& input': { py: 0.5 }
              }
            }}
          />
        </Box>

        {/* Filter Tabs */}
        <Box sx={{ px: 1, py: 0.5, bgcolor: '#fff', borderBottom: '1px solid #e9edef' }}>
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            {FILTER_TABS.map((tab) => (
              <Chip
                key={tab.id}
                label={tab.label}
                size="small"
                onClick={() => setActiveFilter(tab.id)}
                icon={
                  tab.id === 'labels' ? (
                    <KeyboardArrowDown sx={{ fontSize: 16 }} />
                  ) : undefined
                }
                deleteIcon={tab.id === 'labels' ? <KeyboardArrowDown /> : undefined}
                sx={{
                  height: 28,
                  borderRadius: '16px',
                  bgcolor: activeFilter === tab.id ? '#e7fce3' : '#f0f2f5',
                  color: activeFilter === tab.id ? '#008069' : '#54656f',
                  fontWeight: activeFilter === tab.id ? 600 : 400,
                  fontSize: 13,
                  border: 'none',
                  '&:hover': {
                    bgcolor: activeFilter === tab.id ? '#d9f7d4' : '#e9edef'
                  },
                  '& .MuiChip-label': { px: 1.5 }
                }}
              />
            ))}
          </Box>
        </Box>

        {/* Archived Section */}
        {archivedCount > 0 && (
          <ListItem
            component="div"
            onClick={() => setArchivedExpanded(!archivedExpanded)}
            sx={{
              bgcolor: '#fff',
              borderBottom: '1px solid #f0f2f5',
              py: 1.5,
              cursor: 'pointer',
              '&:hover': { bgcolor: '#f5f6f6' }
            }}
          >
            <ListItemAvatar>
              <Avatar sx={{ bgcolor: '#f0f2f5', color: '#54656f' }}>
                <Archive />
              </Avatar>
            </ListItemAvatar>
            <ListItemText
              primary={
                <Typography variant="subtitle2" sx={{ fontWeight: 500, color: '#111b21' }}>
                  Archived
                </Typography>
              }
            />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" sx={{ color: '#667781' }}>
                {archivedCount}
              </Typography>
              {archivedExpanded ? <ExpandLess /> : <ExpandMore />}
            </Box>
          </ListItem>
        )}

        {/* Archived Conversations Collapse */}
        <Collapse in={archivedExpanded}>
          <List sx={{ bgcolor: '#f5f6f6', p: 0 }}>
            {archivedConversations.map((conv) => (
              <ListItem
                key={conv._id}
                component="div"
                onClick={() => fetchMessages(conv._id)}
                sx={{
                  borderBottom: '1px solid #e9edef',
                  py: 1,
                  cursor: 'pointer',
                  '&:hover': { bgcolor: '#ebedef' }
                }}
              >
                <ListItemAvatar>
                  <Avatar sx={{ bgcolor: '#dfe5e7', width: 45, height: 45 }}>
                    {conv.customerName?.charAt(0) || 'C'}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={conv.customerName}
                  secondary={conv.lastMessage}
                  primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                  secondaryTypographyProps={{ variant: 'caption', noWrap: true }}
                />
              </ListItem>
            ))}
          </List>
        </Collapse>

        {/* Conversations List */}
        <List sx={{ flex: 1, overflow: 'auto', p: 0, bgcolor: '#fff' }}>
          {filteredConversations.map((conv) => (
            <ListItem
              key={conv._id}
              component="div"
              selected={selectedConversation?._id === conv._id}
              onClick={() => fetchMessages(conv._id)}
              onContextMenu={(e) => handleContextMenu(e, conv)}
              sx={{
                borderBottom: '1px solid #f0f2f5',
                py: 1,
                px: 1.5,
                cursor: 'pointer',
                '&.Mui-selected': {
                  bgcolor: '#f0f2f5'
                },
                '&:hover': {
                  bgcolor: '#f5f6f6'
                }
              }}
            >
              <ListItemAvatar sx={{ minWidth: 50 }}>
                <Avatar 
                  sx={{ 
                    bgcolor: conv.isGroup ? '#00a884' : '#dfe5e7', 
                    width: 42, 
                    height: 42,
                    color: conv.isGroup ? '#fff' : '#54656f',
                    fontSize: 16
                  }}
                >
                  {conv.isGroup ? <Group /> : (conv.customerName?.charAt(0) || 'C')}
                </Avatar>
              </ListItemAvatar>
              <Box sx={{ flex: 1, minWidth: 0, ml: 0.5, maxWidth: 230, overflow: 'hidden' }}>
                {/* Top Row: Name + Tag + Time */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0, flex: 1 }}>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        fontWeight: 500, 
                        color: '#111b21',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        maxWidth: conv.tags?.length > 0 ? '100px' : '140px',
                        fontSize: 14
                      }}
                    >
                      {conv.customerName || conv.customerPhone}
                    </Typography>
                    {/* Chat Tags - WhatsApp style badge */}
                    {renderChatTags(conv.tags)}
                  </Box>
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      color: conv.unreadCount > 0 ? '#25d366' : '#667781',
                      fontWeight: conv.unreadCount > 0 ? 500 : 400,
                      fontSize: 11,
                      whiteSpace: 'nowrap',
                      flexShrink: 0
                    }}
                  >
                    {formatTime(conv.lastMessageAt)}
                  </Typography>
                </Box>
                
                {/* Bottom Row: Message + Icons */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0 }}>
                    {conv.lastMessageFrom === 'STAFF' && getStatusIcon('READ')}
                    {renderMessageTypeIcon(conv)}
                    <Typography
                      variant="body2"
                      sx={{
                        color: '#667781',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        fontSize: 13,
                        maxWidth: 150
                      }}
                    >
                      {conv.lastMessage}
                    </Typography>
                  </Box>
                  {/* Unread count badge */}
                  {conv.unreadCount > 0 && (
                    <Box
                      sx={{
                        bgcolor: '#25d366',
                        color: '#fff',
                        fontSize: 11,
                        fontWeight: 600,
                        minWidth: 18,
                        height: 18,
                        borderRadius: '9px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        px: 0.5,
                        ml: 0.5
                      }}
                    >
                      {conv.unreadCount}
                    </Box>
                  )}
                </Box>
              </Box>
            </ListItem>
          ))}
          
          {/* Empty state */}
          {filteredConversations.length === 0 && (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="body2" color="textSecondary">
                No conversations found
              </Typography>
            </Box>
          )}
        </List>
      </Paper>

      {/* Right Side - Chat Area */}
      {selectedConversation ? (
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', bgcolor: '#efeae2' }}>
          {/* Chat Header */}
          <Paper
            sx={{
              p: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              borderRadius: 0,
              bgcolor: '#f0f2f5'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar sx={{ bgcolor: '#00a884' }}>
                {selectedConversation.customerName?.charAt(0) || 'C'}
              </Avatar>
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#111b21' }}>
                  {selectedConversation.customerName || selectedConversation.customerPhone}
                </Typography>
                <Typography variant="caption" sx={{ color: '#667781' }}>
                  {selectedConversation.customerPhone}
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              {/* Quick Send Actions */}
              <Tooltip title="Send Campaign">
                <IconButton 
                  size="small" 
                  onClick={handleOpenCampaignDialog}
                  sx={{ color: '#9c27b0' }}
                >
                  <Campaign fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Send Offer">
                <IconButton 
                  size="small" 
                  onClick={handleOpenOfferDialog}
                  sx={{ color: '#4caf50' }}
                >
                  <LocalOffer fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Send Form">
                <IconButton 
                  size="small" 
                  onClick={handleOpenFormDialog}
                  sx={{ color: '#ff9800' }}
                >
                  <Assignment fontSize="small" />
                </IconButton>
              </Tooltip>
              <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
              <Chip
                label={selectedConversation.status}
                size="small"
                color={selectedConversation.status === 'OPEN' ? 'success' : 'default'}
              />
              <Chip
                label={selectedConversation.priority}
                size="small"
                color={
                  selectedConversation.priority === 'URGENT' ? 'error' :
                  selectedConversation.priority === 'HIGH' ? 'warning' : 'default'
                }
              />
              <IconButton onClick={() => setDetailsOpen(true)}>
                <MoreVert />
              </IconButton>
            </Box>
          </Paper>

          {/* Messages Area */}
          <Box
            sx={{
              flex: 1,
              overflow: 'auto',
              p: 2,
              backgroundImage: 'url(https://user-images.githubusercontent.com/15075759/28719144-86dc0f70-73b1-11e7-911d-60d70fcded21.png)',
              backgroundSize: 'cover'
            }}
          >
            {messages.map((msg, index) => {
              const isStaff = msg.senderType === 'STAFF';
              const showDate = index === 0 || 
                formatDate(messages[index - 1].timestamp) !== formatDate(msg.timestamp);

              return (
                <React.Fragment key={msg._id}>
                  {showDate && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
                      <Chip
                        label={formatDate(msg.timestamp)}
                        size="small"
                        sx={{ bgcolor: '#fff', color: '#667781' }}
                      />
                    </Box>
                  )}
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: isStaff ? 'flex-end' : 'flex-start',
                      mb: 1
                    }}
                  >
                    <Paper
                      sx={{
                        maxWidth: '65%',
                        p: 1.5,
                        bgcolor: isStaff ? '#d9fdd3' : '#fff',
                        borderRadius: 2,
                        boxShadow: '0 1px 0.5px rgba(0,0,0,0.13)'
                      }}
                    >
                      {!isStaff && (
                        <Typography variant="caption" sx={{ color: '#00a884', fontWeight: 600 }}>
                          {msg.senderName}
                        </Typography>
                      )}
                      {msg.messageType === 'FILE' ? (
                        <>
                          {/* Inline image preview if it's an image */}
                          {msg.mediaUrl && /\.(jpe?g|png|gif|webp|bmp)$/i.test(msg.mediaUrl) && (
                            <Box sx={{ mb: 0.5 }}>
                              <img
                                src={normalizeMediaUrl(msg.mediaUrl)}
                                alt={msg.content || 'attachment'}
                                style={{
                                  maxWidth: '220px',
                                  maxHeight: '220px',
                                  borderRadius: 8,
                                  display: 'block'
                                }}
                              />
                            </Box>
                          )}
                          <Typography variant="body2" sx={{ color: '#111b21', wordBreak: 'break-all' }}>
                            📎 {msg.content || msg.mediaUrl?.split('/').pop()}
                          </Typography>
                          {msg.mediaUrl && (
                            <Typography variant="caption">
                              <a href={normalizeMediaUrl(msg.mediaUrl)} target="_blank" rel="noreferrer">View / Download</a>
                            </Typography>
                          )}
                        </>
                      ) : (
                        <Typography variant="body2" sx={{ color: '#111b21', whiteSpace: 'pre-wrap' }}>
                          {msg.content}
                        </Typography>
                      )}
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                        <Typography variant="caption" sx={{ color: '#667781', fontSize: 11 }}>
                          {formatTime(msg.timestamp)}
                        </Typography>
                        {isStaff && getStatusIcon(msg.status)}
                      </Box>
                    </Paper>
                  </Box>
                </React.Fragment>
              );
            })}
            <div ref={messagesEndRef} />
          </Box>

          {/* Message Input */}
          <Paper
            sx={{
              p: 1.5,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              borderRadius: 0,
              bgcolor: '#f0f2f5'
            }}
          >
            <IconButton size="small">
              <EmojiEmotions sx={{ color: '#8696a0' }} />
            </IconButton>
            <IconButton size="small" onClick={handleAttachClick}>
              <AttachFile sx={{ color: '#8696a0' }} />
            </IconButton>
            <input
              type="file"
              ref={fileInputRef}
              style={{ display: 'none' }}
              onChange={handleFileChange}
            />
            <TextField
              fullWidth
              multiline
              maxRows={4}
              placeholder="Type a message"
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              sx={{
                '& .MuiOutlinedInput-root': {
                  bgcolor: '#fff',
                  borderRadius: 2,
                  '& fieldset': { border: 'none' }
                }
              }}
            />
            <IconButton
              color="primary"
              onClick={handleSendMessage}
              disabled={!newMessage.trim()}
              sx={{
                bgcolor: '#00a884',
                color: '#fff',
                '&:hover': { bgcolor: '#008069' },
                '&:disabled': { bgcolor: '#e9edef', color: '#8696a0' }
              }}
            >
              <Send />
            </IconButton>
          </Paper>
        </Box>
      ) : (
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: '#f0f2f5'
          }}
        >
          <Box sx={{ textAlign: 'center' }}>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center', 
              gap: 1, 
              mb: 2,
              color: currentProvider.color
            }}>
              {React.cloneElement(currentProvider.icon, { sx: { fontSize: 48 } })}
            </Box>
            <Typography variant="h6" sx={{ color: '#667781', mb: 1 }}>
              {currentProvider.name} CRM Chat
            </Typography>
            <Typography variant="body2" sx={{ color: '#8696a0', mb: 3 }}>
              Select a conversation to start messaging
            </Typography>
            
            {/* Quick Actions */}
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Chip
                icon={<Campaign />}
                label="Send Campaign"
                onClick={() => {/* Navigate to campaigns */}}
                sx={{ cursor: 'pointer' }}
              />
              <Chip
                icon={<LocalOffer />}
                label="Send Offer"
                onClick={() => {/* Navigate to offers */}}
                sx={{ cursor: 'pointer' }}
              />
              <Chip
                icon={<Assignment />}
                label="Send Form"
                onClick={() => {/* Navigate to forms */}}
                sx={{ cursor: 'pointer' }}
              />
            </Box>
          </Box>
        </Box>
      )}

      {/* Conversation Details Dialog */}
      <Dialog open={detailsOpen} onClose={() => setDetailsOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Conversation Details
          <IconButton
            onClick={() => setDetailsOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {selectedConversation && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box>
                <Typography variant="subtitle2" color="textSecondary">Customer</Typography>
                <Typography variant="body1">{selectedConversation.customerName}</Typography>
                <Typography variant="body2" color="textSecondary">
                  {selectedConversation.customerPhone}
                </Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="textSecondary">Status</Typography>
                <FormControl fullWidth size="small" sx={{ mt: 1 }}>
                  <Select value={selectedConversation.status}>
                    <MenuItem value="OPEN">Open</MenuItem>
                    <MenuItem value="PENDING">Pending</MenuItem>
                    <MenuItem value="RESOLVED">Resolved</MenuItem>
                    <MenuItem value="CLOSED">Closed</MenuItem>
                  </Select>
                </FormControl>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="textSecondary">Priority</Typography>
                <FormControl fullWidth size="small" sx={{ mt: 1 }}>
                  <Select value={selectedConversation.priority}>
                    <MenuItem value="LOW">Low</MenuItem>
                    <MenuItem value="MEDIUM">Medium</MenuItem>
                    <MenuItem value="HIGH">High</MenuItem>
                    <MenuItem value="URGENT">Urgent</MenuItem>
                  </Select>
                </FormControl>
              </Box>
              {selectedConversation.aiSentiment && (
                <Box>
                  <Typography variant="subtitle2" color="textSecondary">AI Sentiment</Typography>
                  <Chip
                    label={selectedConversation.aiSentiment}
                    size="small"
                    color={
                      selectedConversation.aiSentiment === 'POSITIVE' ? 'success' :
                      selectedConversation.aiSentiment === 'NEGATIVE' ? 'error' : 'default'
                    }
                    sx={{ mt: 1 }}
                  />
                </Box>
              )}
              
              {/* Tags / Shared Items */}
              <Box>
                <Typography variant="subtitle2" color="textSecondary">Tags & Shared Items</Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                  {selectedConversation.tags?.map((tag, idx) => {
                    const tagConfig = CHAT_TAGS[tag];
                    if (!tagConfig) return null;
                    return (
                      <Chip
                        key={idx}
                        icon={tagConfig.icon}
                        label={tagConfig.label}
                        size="small"
                        sx={{ 
                          bgcolor: `${tagConfig.color}20`,
                          color: tagConfig.color,
                          '& .MuiChip-icon': { color: tagConfig.color }
                        }}
                      />
                    );
                  })}
                  {(!selectedConversation.tags || selectedConversation.tags.length === 0) && (
                    <Typography variant="body2" color="textSecondary">No tags</Typography>
                  )}
                </Box>
              </Box>

              {/* Provider Info */}
              <Box>
                <Typography variant="subtitle2" color="textSecondary">Channel</Typography>
                <Chip
                  icon={selectedConversation.provider === 'WHATSAPP' ? <WhatsApp /> : <KooppaiIcon />}
                  label={selectedConversation.provider === 'WHATSAPP' ? 'WhatsApp' : 'KooppAI'}
                  size="small"
                  sx={{ 
                    mt: 1,
                    bgcolor: selectedConversation.provider === 'WHATSAPP' ? '#25D36620' : '#6366f120',
                    color: selectedConversation.provider === 'WHATSAPP' ? '#25D366' : '#6366f1',
                    '& .MuiChip-icon': { 
                      color: selectedConversation.provider === 'WHATSAPP' ? '#25D366' : '#6366f1' 
                    }
                  }}
                />
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsOpen(false)}>Close</Button>
          <Button variant="contained" color="primary">Save Changes</Button>
        </DialogActions>
      </Dialog>

      {/* Context Menu for Conversations */}
      <Menu
        open={contextMenu.open}
        onClose={handleCloseContextMenu}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenu.mouseX !== null && contextMenu.mouseY !== null
            ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
            : undefined
        }
        PaperProps={{ sx: { minWidth: 200, boxShadow: 3 } }}
      >
        <MenuItem 
          onClick={() => {
            if (contextMenu.conversation) {
              toggleConversationProperty(contextMenu.conversation._id, 'isFavourite');
            }
            handleCloseContextMenu();
          }}
        >
          {contextMenu.conversation?.isFavourite ? (
            <>
              <Star sx={{ mr: 1, color: '#ffc107' }} fontSize="small" />
              Remove from Favourites
            </>
          ) : (
            <>
              <StarBorder sx={{ mr: 1 }} fontSize="small" />
              Add to Favourites
            </>
          )}
        </MenuItem>
        <MenuItem 
          onClick={() => {
            if (contextMenu.conversation) {
              toggleConversationProperty(contextMenu.conversation._id, 'isMuted');
            }
            handleCloseContextMenu();
          }}
        >
          <VolumeOff sx={{ mr: 1 }} fontSize="small" />
          {contextMenu.conversation?.isMuted ? 'Unmute' : 'Mute notifications'}
        </MenuItem>
        <MenuItem 
          onClick={() => {
            if (contextMenu.conversation) {
              toggleConversationProperty(contextMenu.conversation._id, 'isArchived');
            }
            handleCloseContextMenu();
          }}
        >
          <Archive sx={{ mr: 1 }} fontSize="small" />
          {contextMenu.conversation?.isArchived ? 'Unarchive' : 'Archive chat'}
        </MenuItem>
        <Divider />
        <MenuItem disabled>
          <Label sx={{ mr: 1 }} fontSize="small" />
          Add Label
        </MenuItem>
        <MenuItem 
          onClick={() => {
            if (contextMenu.conversation) {
              setSelectedConversation(contextMenu.conversation);
              setDetailsOpen(true);
            }
            handleCloseContextMenu();
          }}
        >
          <MoreVert sx={{ mr: 1 }} fontSize="small" />
          View Details
        </MenuItem>
        <Divider />
        <MenuItem 
          onClick={() => {
            if (contextMenu.conversation) {
              deleteConversation(contextMenu.conversation._id);
            }
            handleCloseContextMenu();
          }}
          sx={{ color: 'error.main' }}
        >
          <Close sx={{ mr: 1 }} fontSize="small" />
          Delete Chat
        </MenuItem>
      </Menu>

      {/* Campaign Dialog */}
      <Dialog open={campaignDialog} onClose={() => setCampaignDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Send Campaign to {selectedConversation?.customerName}</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Select Campaign</InputLabel>
            <Select
              value={selectedItem?._id || ''}
              onChange={(e) => setSelectedItem(campaigns.find(c => c._id === e.target.value))}
              label="Select Campaign"
            >
              {campaigns.map(campaign => (
                <MenuItem key={campaign._id} value={campaign._id}>
                  {campaign.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {selectedItem && (
            <Box sx={{ mt: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
              <Typography variant="body2" color="textSecondary">
                {selectedItem.description || selectedItem.message?.substring(0, 100)}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCampaignDialog(false)}>Cancel</Button>
          <Button 
            variant="contained" 
            onClick={handleSendCampaign} 
            disabled={!selectedItem || sending}
            startIcon={<Campaign />}
          >
            {sending ? 'Sending...' : 'Send Campaign'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Offer Dialog */}
      <Dialog open={offerDialog} onClose={() => setOfferDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Send Offer to {selectedConversation?.customerName}</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Select Offer</InputLabel>
            <Select
              value={selectedItem?._id || ''}
              onChange={(e) => setSelectedItem(offers.find(o => o._id === e.target.value))}
              label="Select Offer"
            >
              {offers.map(offer => (
                <MenuItem key={offer._id} value={offer._id}>
                  {offer.title} - {offer.discountType === 'PERCENTAGE' ? `${offer.discountValue}%` : `₹${offer.discountValue}`} OFF
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {selectedItem && (
            <Box sx={{ mt: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
              <Typography variant="body2">{selectedItem.description}</Typography>
              <Typography variant="caption" color="textSecondary">
                Valid: {new Date(selectedItem.validFrom).toLocaleDateString()} - {new Date(selectedItem.validTo).toLocaleDateString()}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOfferDialog(false)}>Cancel</Button>
          <Button 
            variant="contained" 
            color="success"
            onClick={handleSendOffer} 
            disabled={!selectedItem || sending}
            startIcon={<LocalOffer />}
          >
            {sending ? 'Sending...' : 'Send Offer'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Form Dialog */}
      <Dialog open={formDialog} onClose={() => setFormDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Send Form to {selectedConversation?.customerName}</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Select Form</InputLabel>
            <Select
              value={selectedItem?._id || ''}
              onChange={(e) => setSelectedItem(forms.find(f => f._id === e.target.value))}
              label="Select Form"
            >
              {forms.map(form => (
                <MenuItem key={form._id} value={form._id}>
                  {form.title || form.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {selectedItem && (
            <Box sx={{ mt: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
              <Typography variant="body2">{selectedItem.description}</Typography>
              <Typography variant="caption" color="textSecondary">
                {selectedItem.fields?.length || 0} fields
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFormDialog(false)}>Cancel</Button>
          <Button 
            variant="contained" 
            color="warning"
            onClick={handleSendForm} 
            disabled={!selectedItem || sending}
            startIcon={<Assignment />}
          >
            {sending ? 'Sending...' : 'Send Form'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WhatsAppChat;
